import React, { useMemo, useState } from 'react';

import { Box, Button, MenuItem, MenuList, Typography } from '@material-ui/core';
import type { GridColDef } from '@mui/x-data-grid';
import { FiArchive, FiCopy, FiEdit3, FiFolder } from 'react-icons/fi';
import { HiOutlineTrash } from 'react-icons/hi';
import { LuSend } from 'react-icons/lu';
import { TbPencil } from 'react-icons/tb';

import {
  AppDataGridWithSavedPage,
  AppLink,
  ButtonTitleWithLoading,
  DetailsIcon,
  FormattedDescription,
  GridActions,
} from '@vyce/core/src/components';
import { FormTemplateDoc } from '@vyce/core/src/api/types';
import { buildFormUrlSegment, getUKFormattedDate } from '@vyce/core/src/utils';

import { DraggableTemplateTitleCell } from './DraggableTemplateTitleCell';

interface Props {
  rows: FormTemplateDoc[];
  loading: boolean;
  total: number;
  height: string;
  setOffset: (offset: number) => void;
  enableDragAndDrop?: boolean;
  dragType?: string;
  withActions?: boolean;
  isArchivedView?: boolean;
  fillingInTemplateId?: string | null;
  fillingOutTemplateId?: string | null;
  duplicatingTemplateId?: string | null;
  onFillIn?: (template: FormTemplateDoc) => void;
  onSend?: (template: FormTemplateDoc) => void;
  onEditTemplate?: (template: FormTemplateDoc) => void;
  onDuplicateTemplate?: (template: FormTemplateDoc) => void;
  onArchiveTemplate?: (template: FormTemplateDoc) => void;
  onChangeFolder?: (template: FormTemplateDoc) => void;
  onDeleteTemplate?: (template: FormTemplateDoc) => void;
  pageSize?: number;
  rowsPerPageOptions?: number[];
  onPageChange?: (page: number) => void;
}

export const TemplatesDataGrid: React.FC<Props> = ({
  rows,
  loading,
  total,
  height,
  setOffset,
  enableDragAndDrop = false,
  dragType,
  withActions = false,
  isArchivedView = false,
  fillingInTemplateId = null,
  fillingOutTemplateId = null,
  duplicatingTemplateId = null,
  onFillIn,
  onSend,
  onEditTemplate,
  onDuplicateTemplate,
  onArchiveTemplate,
  onChangeFolder,
  onDeleteTemplate,
  pageSize,
  rowsPerPageOptions,
  onPageChange,
}) => {
  const [closeGridAction, setCloseGridAction] = useState(false);

  const resetCloseStatus = () => {
    setCloseGridAction(true);
    setTimeout(() => setCloseGridAction(false), 100);
  };

  const getUpdatedAt = (row: FormTemplateDoc) => {
    const u = row.updatedAt;
    if (!u) return '-';
    if (typeof u === 'object' && '_seconds' in u) {
      return getUKFormattedDate((u as { _seconds: number })._seconds * 1000);
    }
    if (u instanceof Date) return getUKFormattedDate(u);
    return '-';
  };

  const actionsColumn: GridColDef = useMemo(
    () => ({
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        const template = params.row as FormTemplateDoc;
        const archived = template.archived === true;
        return (
          <Box display="flex" width="100%" alignItems="center" justifyContent="flex-end" gridGap={8}>
            {!archived && onFillIn && (
              <Button
                variant="outlined"
                size="small"
                style={{ width: 100 }}
                color="primary"
                startIcon={<TbPencil size={16} />}
                onClick={() => onFillIn(template)}
                disabled={fillingInTemplateId === template.id}
              >
                <ButtonTitleWithLoading
                  title="Fill in"
                  loading={fillingInTemplateId === template.id}
                  loaderVariant="primary"
                />
              </Button>
            )}

            {!archived && onSend && (
              <Button
                variant="outlined"
                size="small"
                style={{ width: 100 }}
                color="primary"
                startIcon={<LuSend size={16} />}
                onClick={() => onSend(template)}
                disabled={fillingOutTemplateId === template.id}
              >
                <ButtonTitleWithLoading
                  title="Send"
                  loading={fillingOutTemplateId === template.id}
                  loaderVariant="primary"
                />
              </Button>
            )}

            <GridActions close={closeGridAction}>
              <MenuList>
                {onEditTemplate && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onEditTemplate(template);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiEdit3 size={14} /> Edit template
                    </Box>
                  </MenuItem>
                )}

                {onDuplicateTemplate && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onDuplicateTemplate(template);
                    }}
                    disabled={duplicatingTemplateId === template.id}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiCopy size={14} /> Duplicate template
                    </Box>
                  </MenuItem>
                )}

                {onChangeFolder && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onChangeFolder(template);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiFolder size={14} /> Change folder
                    </Box>
                  </MenuItem>
                )}

                {onArchiveTemplate && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onArchiveTemplate(template);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <FiArchive size={14} /> {archived ? 'Unarchive template' : 'Archive template'}
                    </Box>
                  </MenuItem>
                )}

                {onDeleteTemplate && (
                  <MenuItem
                    onClick={() => {
                      resetCloseStatus();
                      onDeleteTemplate(template);
                    }}
                  >
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <HiOutlineTrash size={14} /> Delete template
                    </Box>
                  </MenuItem>
                )}
              </MenuList>
            </GridActions>
          </Box>
        );
      },
    }),
    [
      closeGridAction,
      duplicatingTemplateId,
      fillingInTemplateId,
      fillingOutTemplateId,
      onArchiveTemplate,
      onChangeFolder,
      onDeleteTemplate,
      onDuplicateTemplate,
      onEditTemplate,
      onFillIn,
      onSend,
    ]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Form Title',
        flex: 0.25,
        minWidth: 200,
        disableColumnMenu: true,
        renderCell: params => {
          const template = params.row as FormTemplateDoc;
          if (enableDragAndDrop && dragType) {
            return (
              <DraggableTemplateTitleCell
                template={template}
                title={template.title}
                dragType={dragType}
              />
            );
          }
          return (
            <Box
              display="flex"
              width="100%"
              gridGap={8}
              alignItems="center"
              style={{ cursor: 'pointer' }}
            >
              <Box
                flexGrow={1}
                minWidth={0}
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                }}
              >
                <AppLink
                  to={`/forms/form-templates/${buildFormUrlSegment(template.title, template.id || '')}/edit`}
                >
                  {template.title}
                </AppLink>
              </Box>

              {template.description && (
                <DetailsIcon
                  content={<FormattedDescription text={template.description} variant="caption" />}
                  type="info"
                  richContent
                />
              )}
            </Box>
          );
        },
      },
      {
        field: 'createdByName',
        headerName: 'Assigned by',
        flex: 0.2,
        minWidth: 150,
        disableColumnMenu: true,
        renderCell: params => (
          <Typography style={{ fontWeight: 500, fontSize: 13 }}>
            {params.row.createdByName || '-'}
          </Typography>
        ),
      },
      {
        field: 'folderName',
        headerName: 'Folder',
        flex: 0.12,
        width: 160,
        sortable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const folderName = params.row.folderName;
          const folderId = params.row.folderId;
          if (!folderName || !folderId) {
            return <Typography style={{ fontWeight: 500, fontSize: 13 }}>-</Typography>;
          }
          return (
            <AppLink
              to={`/forms/form-templates/folders/${buildFormUrlSegment(folderName, folderId)}`}
            >
              {folderName}
            </AppLink>
          );
        },
      },
      {
        field: 'updatedAt',
        headerName: 'Lastly',
        flex: 0.15,
        minWidth: 120,
        disableColumnMenu: true,
        renderCell: params => (
          <Typography style={{ fontWeight: 500, fontSize: 13 }}>
            {getUpdatedAt(params.row as FormTemplateDoc)}
          </Typography>
        ),
      },
      ...(withActions ? [actionsColumn] : []),
    ],
    [actionsColumn, dragType, enableDragAndDrop, withActions]
  );

  return (
    <AppDataGridWithSavedPage
      rows={rows}
      getRowId={row => row.id || ''}
      columns={columns}
      noPaper
      rowHeight={88}
      height={height}
      loading={loading}
      rowCount={total}
      pageSize={pageSize ?? 100}
      rowsPerPageOptions={rowsPerPageOptions ?? [100]}
      onPageChange={onPageChange ?? (() => {})}
      disableSelectionOnClick
      setOffset={setOffset}
    />
  );
};
